import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiHeart } from 'react-icons/fi';
import config from '../config';
import '../styles/navbar.css';

const Navbar = () => {
  const { isAuthenticated, user, isAdmin, isModerator, logout } = useAuth();
  const navigate = useNavigate();
  // Changed to track which specific dropdown is active (null, 'about', 'community', 'user')
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Refs for dropdown containers to handle click outside
  const navRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setActiveDropdown(null);
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(prev => prev === name ? null : name);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to render dropdown items
  const renderDropdownItem = (to, text) => (
    <Link
      to={to}
      className="dropdown-item"
      onClick={() => setActiveDropdown(null)}
    >
      {text}
    </Link>
  );

  return (
    <nav className="navbar navbar-expand-xl navbar-dark navbar-custom" ref={navRef}>
      <div className="container-fluid">
        <Link className="navbar-brand text-light" to="/home">
          Electrospinning-Data
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <NavLink className={({ isActive }) => `nav-link text-light ${isActive ? 'active' : ''}`} to="/home">
                Home
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className={({ isActive }) => `nav-link text-light ${isActive ? 'active' : ''}`} to="/dataset">
                Dataset
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className={({ isActive }) => `nav-link text-light ${isActive ? 'active' : ''}`} to="/dataSubmission">
                Contribute Data
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className={({ isActive }) => `nav-link text-light ${isActive ? 'active' : ''}`} to="/virtual-lab">
                Virtual Lab
              </NavLink>
            </li>
          </ul>

          <ul className="navbar-nav ms-auto align-items-center">
            {/* Community Dropdown */}
            <li
              className="nav-item dropdown-container"
              onMouseEnter={() => setActiveDropdown('community')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="nav-link text-light dropdown-toggle-btn"
                onClick={() => toggleDropdown('community')}
              >
                Community <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'community' && (
                <div className="custom-dropdown-menu dropdown-menu-right">
                  {renderDropdownItem('/community', 'Getting Involved')}
                </div>
              )}
            </li>

            {/* About Dropdown */}
            <li
              className="nav-item dropdown-container"
              onMouseEnter={() => setActiveDropdown('about')}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                className="nav-link text-light dropdown-toggle-btn"
                onClick={() => toggleDropdown('about')}
              >
                About <span className="dropdown-arrow">▼</span>
              </button>
              {activeDropdown === 'about' && (
                <div className="custom-dropdown-menu dropdown-menu-right">
                  {renderDropdownItem('/about', 'About')}
                  {renderDropdownItem('/partners', 'Partners and support')}
                  {renderDropdownItem('/how-to-cite', 'How to cite')}
                  {renderDropdownItem('/publications', 'Publications')}
                  {renderDropdownItem('/terms', 'Terms of use')}
                  {renderDropdownItem('/privacy', 'Privacy policy')}
                  <div className="dropdown-divider"></div>
                  <a
                    href={config.docsUrl}
                    className="dropdown-item"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setActiveDropdown(null)}
                  >
                    Documentation
                  </a>
                  <div className="dropdown-divider"></div>
                  {renderDropdownItem('/contact', 'Contact')}
                </div>
              )}
            </li>

            {/* User dropdown */}
            {isAuthenticated && (
              <li
                className="nav-item dropdown-container user-dropdown"
                onMouseEnter={() => setActiveDropdown('user')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className="nav-link text-light user-button"
                  onClick={() => toggleDropdown('user')}
                >
                  <span className="user-avatar">{(user?.name || user?.email || '?').charAt(0).toUpperCase()}</span>
                  <span className="user-email-text">{user?.name || user?.email}</span>
                  {isAdmin() && <span className="admin-badge">Admin</span>}
                  {isModerator() && <span className="admin-badge" style={{ backgroundColor: '#17a2b8' }}>Moderator</span>}
                  <span className="dropdown-arrow">▼</span>
                </button>

                {activeDropdown === 'user' && (
                  <div className="custom-dropdown-menu user-menu-right">
                    <div className="dropdown-header">
                      <div className="dropdown-username">{user?.name || user?.email}</div>
                      {user?.name && <div className="dropdown-email">{user?.email}</div>}
                    </div>
                    <div className="dropdown-divider"></div>
                    {renderDropdownItem('/my-submissions', 'My Contributions')}
                    {renderDropdownItem('/profile', 'Profile Settings')}
                    {(isAdmin() || isModerator()) && renderDropdownItem('/admin', 'Dashboard')}
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                )}
              </li>
            )}
            {!isAuthenticated && (
              <li className="nav-item">
                <Link className="nav-link text-light" to="/login">
                  Login
                </Link>
              </li>
            )}

            {/* Support — always the last (right-most) item */}
            <li className="nav-item ms-2">
              <Link to="/support" className="nav-support-btn">
                <FiHeart size={13} /> Support
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
